import APIService from 'services/APIService'
import qs from 'qs'

const api = APIService()

/**
 * Check if the current user has access to Reporting for a building passport
 * @param {string} bpId - Building passport ID
 * @returns {Promise} API response with access information
 */
export const checkAccess = bpId => {
  return api.get(`/building-passport/${bpId}/reporting/access`)
}

/**
 * Trigger generation of a new Reporting report
 * @param {string} bpId - Building passport ID
 * @returns {Promise} API response with the new report data (status 202)
 */
export const triggerReport = (bpId, reportType = 'insurance') => {
  return api.post(`/building-passport/${bpId}/reporting/reports`, { reportType })
}

/**
 * Get a single Reporting report
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @returns {Promise} API response with the report data
 */
export const getReport = (bpId, reportId) => {
  return api.get(`/building-passport/${bpId}/reporting/reports/${reportId}`)
}

/**
 * Get all Reporting reports for a building passport (paginated)
 * @param {string} bpId - Building passport ID
 * @param {Object} options - Pagination options
 * @param {number} options.page - Page number
 * @param {number} options.perPage - Items per page
 * @returns {Promise} API response with reports list and pagination meta
 */
export const getReports = (bpId, { page = 1, perPage = 10, reportType } = {}) => {
  const queryString = qs.stringify({ page, per_page: perPage, ...(reportType && { report_type: reportType }) }, { addQueryPrefix: true })
  return api.get(`/building-passport/${bpId}/reporting/reports${queryString}`)
}

/**
 * Send Reporting report via email
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @param {Object} payload - Email data
 * @param {string} payload.email - Recipient email address
 * @param {string} payload.message - Optional message to include
 * @returns {Promise} API response
 */
export const sendReportEmail = (bpId, reportId, { email, message }) => {
  return api.post(`/building-passport/${bpId}/reporting/reports/${reportId}/email`, {
    email,
    message
  })
}

/**
 * Get a publicly shared Reporting report by token
 * @param {string} token - Public access token
 * @returns {Promise} API response with the report data
 */
export const getPublicReport = token => {
  return api.get(`/reporting/reports/public/${token}`)
}

/**
 * Download a publicly-shared report as a Word document.
 * No auth required — the token gates access.
 */
export const exportPublicWord = token => {
  return api.get(`/reporting/reports/public/${token}/word`, { responseType: 'blob' })
}

/**
 * Download a publicly-shared Insurance Guru report as a PDF.
 * Returns 400 for CPSE reports (Word-only).
 */
export const exportPublicPdf = token => {
  return api.get(`/reporting/reports/public/${token}/pdf`, { responseType: 'blob' })
}

/**
 * Log a public-viewer source click AND fetch a fresh pre-signed file URL.
 * Matches the per-file audit-log pattern used by ExternalAccess file shares.
 */
export const logPublicSourceView = (token, fileId) => {
  return api.post(`/reporting/reports/public/${token}/sources/${fileId}/view`)
}

/**
 * Public, token-gated list of the evidence files referenced by a report.
 * No auth — the token is the credential. Individual files are opened via
 * logPublicSourceView (which returns a fresh pre-signed URL).
 */
export const fetchReportEvidence = token => {
  return api.get(`/reporting/reports/public/${token}/evidence`)
}

/**
 * Per-recipient report shares — every download / view / source click made via
 * the returned share_token is attributed to the recipient's email in the
 * insurance_guru_audit_logs table.
 */
export const listReportShares = (bpId, reportId) =>
  api.get(`/building-passport/${bpId}/reporting/reports/${reportId}/shares`)

/**
 * @param {{ shares: Array<{ email: string, name?: string }> }} payload
 */
export const createReportShares = (bpId, reportId, payload) =>
  api.post(`/building-passport/${bpId}/reporting/reports/${reportId}/shares`, payload)

export const revokeReportShare = (bpId, reportId, shareId) =>
  api.delete(`/building-passport/${bpId}/reporting/reports/${reportId}/shares/${shareId}`)

/**
 * Update a single answer in a report
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @param {string} questionId - Question ID (e.g., 'Q1')
 * @param {string} answer - New answer text
 * @returns {Promise} API response with updated report
 */
export const updateAnswer = (bpId, reportId, questionId, answer) => {
  return api.patch(
    `/building-passport/${bpId}/reporting/reports/${reportId}/answers/${questionId}`,
    { answer }
  )
}

/**
 * Update Particulars for a CPSE.7 report (bulk update of all 7 fields)
 * @param {string} bpId
 * @param {string} reportId
 * @param {Object} particulars - camelCase keys: seller, buyer, property, transaction, sellerSolicitors, buyerSolicitors, date
 * @returns {Promise}
 */
export const updateParticulars = (bpId, reportId, particulars) => {
  return api.patch(
    `/building-passport/${bpId}/reporting/reports/${reportId}/particulars`,
    { particulars }
  )
}

/**
 * Regenerate a single answer with feedback
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @param {string} questionId - Question ID (e.g., 'Q1')
 * @param {Object} options - Regeneration options
 * @param {string} options.feedback - Feedback type: 'try_again', 'more_detailed', 'more_concise', 'custom'
 * @param {string} options.customInstruction - Custom instruction (required if feedback is 'custom')
 * @returns {Promise} API response (202 - regeneration started)
 */
export const regenerateAnswer = (bpId, reportId, questionId, { feedback, customInstruction, subKey }) => {
  return api.post(
    `/building-passport/${bpId}/reporting/reports/${reportId}/answers/${questionId}/regenerate`,
    {
      feedback,
      custom_instruction: customInstruction,
      sub_key: subKey ?? null
    }
  )
}

/**
 * Clone a report as a new version
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @returns {Promise} API response with the new cloned report
 */
export const cloneReport = (bpId, reportId) => {
  return api.post(`/building-passport/${bpId}/reporting/reports/${reportId}/clone`)
}

/**
 * Export report as PDF
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @returns {Promise} Blob response for PDF download
 */
export const exportPdf = (bpId, reportId) => {
  return api.get(
    `/building-passport/${bpId}/reporting/reports/${reportId}/pdf`,
    { responseType: 'blob' }
  )
}

/**
 * Export report as an editable Word document (.docx)
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @returns {Promise} Blob response for Word download
 */
export const exportWord = (bpId, reportId) => {
  return api.get(
    `/building-passport/${bpId}/reporting/reports/${reportId}/word`,
    { responseType: 'blob' }
  )
}

/**
 * Export report as CSV
 * @param {string} bpId - Building passport ID
 * @param {string} reportId - Report ID
 * @returns {Promise} Blob response for CSV download
 */
export const exportCsv = (bpId, reportId) => {
  return api.get(
    `/building-passport/${bpId}/reporting/reports/${reportId}/csv`,
    { responseType: 'blob' }
  )
}

export const getReportTypes = () =>
  api.get('/report-types')

/**
 * BP compliance audit — single-building equivalent of the portfolio audit.
 * Backed by /building-passport/{bp}/audit(s) endpoints.
 */
export const triggerBpAudit = bpId =>
  api.post(`/building-passport/${bpId}/audit`)

export const listBpAudits = bpId =>
  api.get(`/building-passport/${bpId}/audits`)

export const getBpAudit = (bpId, auditId) =>
  api.get(`/building-passport/${bpId}/audit/${auditId}`)

export const exportBpAuditPdf = (bpId, auditId) =>
  api.get(`/building-passport/${bpId}/audits/${auditId}/export/pdf`, { responseType: 'blob' })

export const exportPortfolioReportPdf = () =>
  api.get('/me/portfolio-report/export/pdf', { responseType: 'blob' })

export const exportKpiReportPdf = ({ period, month, from, to, origin, portfolioId, estateId, buildingPassportId, assigneeId } = {}) =>
  api.get('/me/kpi-report/export/pdf', {
    params: {
      ...(assigneeId ? { assigneeId } : {}),
      ...(period ? { period } : {}),
      ...(month ? { month } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(origin ? { origin } : {}),
      ...(portfolioId ? { portfolioId } : {}),
      ...(estateId ? { estateId } : {}),
      ...(buildingPassportId ? { buildingPassportId } : {}),
    },
    responseType: 'blob',
  })

export const getDocumentSuggestions = (bpId, reportType = 'insurance') =>
  api.get(`/building-passport/${bpId}/reporting/document-suggestions`, { params: { reportType } })

/**
 * Summary of report activity grouped by report type slug.
 * Used by the ReportTypesPage to render the status chip on each type card in a
 * single request instead of one call per type.
 */
export const getReportStatusByType = bpId =>
  api.get(`/building-passport/${bpId}/reporting/reports/status-by-type`)
